/* eslint-disable react/no-danger */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useParams, useHistory } from 'react-router';
import { IconType } from 'react-icons';
import {
  FaChevronDown,
  FaChevronLeft,
  FaDownload,
  FaEnvelope,
  FaExternalLinkAlt,
  FaFacebook,
  FaFilePdf,
  FaGlobe,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
} from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { MetaTags, Modal } from '@components';
import { useSponsorProfileQuery, useToggle } from '@hooks';
import { ProductData, SponsorProfileData } from '@common/types/api';

import * as S from './styles';
import ContactUsModal from './ContactModal';
import ProductModal from './ProductModal';
import SponsorVideos from './SponsorVideos';

type SponsorProfileParams = { sponsorId: string };

const SPONSORS_TITLE_KEY = 'pages.sponsors.title';

const SOCIAL_LINKS: {
  key: keyof SponsorProfileData;
  Icon: IconType;
  label: string;
}[] = [
  { key: 'linkedin', Icon: FaLinkedin, label: 'LinkedIn' },
  { key: 'instagram', Icon: FaInstagram, label: 'Instagram' },
  { key: 'facebook', Icon: FaFacebook, label: 'Facebook' },
  { key: 'youtube', Icon: FaYoutube, label: 'YouTube' },
];

const formatWebsite = (url: string) =>
  url.replace(/^https?:\/\//, '').replace(/\/$/, '');

const SkeletonLoader = () => (
  <>
    <S.SkeletonHero>
      <Skeleton width="100%" height="100%" style={{ display: 'block' }} />
    </S.SkeletonHero>
    <S.SkeletonContent>
      <S.SkeletonMain>
        <S.Card>
          <Skeleton width="30%" height={20} style={{ marginBottom: 14 }} />
          <Skeleton count={4} />
        </S.Card>
        <S.Card>
          <Skeleton width="25%" height={20} style={{ marginBottom: 14 }} />
          <Skeleton height={200} />
        </S.Card>
      </S.SkeletonMain>
      <S.SkeletonSidebar>
        <S.SidebarCard>
          <Skeleton width="50%" height={16} style={{ marginBottom: 14 }} />
          <Skeleton width="80%" />
        </S.SidebarCard>
        <S.SidebarCard>
          <Skeleton width="50%" height={16} style={{ marginBottom: 14 }} />
          <Skeleton height={36} />
        </S.SidebarCard>
      </S.SkeletonSidebar>
    </S.SkeletonContent>
  </>
);

type HeroBannerSectionProps = {
  data: SponsorProfileData;
  categoryColor: string;
  onGoBack: () => void;
  onContact: () => void;
};

const HeroBannerSection = ({
  data,
  categoryColor,
  onGoBack,
  onContact,
}: HeroBannerSectionProps) => {
  const { t } = useTranslation();

  return (
    <S.HeroBanner>
      <S.HeroBannerBackground $color={categoryColor} $banner={data.banner}>
        {!data.banner && (
          <S.HeroBannerPattern
            viewBox="0 0 1200 400"
            preserveAspectRatio="xMidYMid slice"
          >
            <defs>
              <pattern
                id="grid"
                width="60"
                height="60"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 60 0 L 0 0 0 60"
                  fill="none"
                  stroke={categoryColor}
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
            <circle
              cx="300"
              cy="200"
              r="180"
              fill="none"
              stroke={categoryColor}
              strokeWidth="0.8"
              opacity="0.3"
            />
            <circle
              cx="300"
              cy="200"
              r="120"
              fill="none"
              stroke={categoryColor}
              strokeWidth="0.5"
              opacity="0.4"
            />
            <circle
              cx="900"
              cy="150"
              r="140"
              fill="none"
              stroke={categoryColor}
              strokeWidth="0.6"
              opacity="0.2"
            />
          </S.HeroBannerPattern>
        )}
      </S.HeroBannerBackground>

      <S.HeroBannerOverlay />

      <S.HeroBreadcrumb>
        <S.BackButton type="button" onClick={onGoBack}>
          <FaChevronLeft size={14} />
        </S.BackButton>
        <S.BreadcrumbText>{t(SPONSORS_TITLE_KEY)}</S.BreadcrumbText>
        <S.BreadcrumbSeparator>/</S.BreadcrumbSeparator>
        <S.BreadcrumbCurrent>{data.name}</S.BreadcrumbCurrent>
      </S.HeroBreadcrumb>

      {data.category?.data && (
        <S.CategoryBadge $color={categoryColor}>
          &#9670; {data.category.data.name}
        </S.CategoryBadge>
      )}

      <S.HeroContent>
        <S.HeroLeft>
          <S.HeroLogo>
            <img src={data.logo} alt={data.name} />
          </S.HeroLogo>

          <S.HeroInfo>
            <h1>{data.name}</h1>
            <S.HeroMeta>
              {data.website && (
                <S.HeroMetaItem>
                  <FaGlobe size={11} />
                  {formatWebsite(data.website)}
                </S.HeroMetaItem>
              )}
            </S.HeroMeta>
          </S.HeroInfo>
        </S.HeroLeft>

        <S.HeroActions>
          {data.website && (
            <S.HeroCTAButton
              type="button"
              $color={categoryColor}
              onClick={() => window.open(data.website || '', '_blank')}
            >
              <FaExternalLinkAlt size={12} />
              {t('pages.sponsors.website')}
            </S.HeroCTAButton>
          )}

          <S.HeroGhostButton type="button" onClick={onContact}>
            <FaEnvelope size={12} />
            Fale conosco
          </S.HeroGhostButton>
        </S.HeroActions>
      </S.HeroContent>
    </S.HeroBanner>
  );
};

const SponsorProfile = () => {
  const { sponsorId } = useParams<SponsorProfileParams>();
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();

  const [activeProduct, setActiveProduct] = useState<ProductData | undefined>();
  const [showFullAbout, setShowFullAbout] = useState(false);
  const [aboutNeedsExpand, setAboutNeedsExpand] = useState(false);
  const aboutRef = useRef<HTMLDivElement>(null);

  const sponsorProfileQuery = useSponsorProfileQuery(sponsorId);

  const handleSelectProduct = (product: ProductData) => () =>
    setActiveProduct(product);

  const [shouldDisplayContactUs, toggleShouldDisplayContactUs] =
    useToggle(false);
  const handleCloseModal = useCallback(() => setActiveProduct(undefined), []);

  const { data } = sponsorProfileQuery;
  const categoryColor =
    data?.category?.data?.color || theme.colors.brand.primary.default;

  useEffect(() => {
    if (aboutRef.current) {
      setAboutNeedsExpand(aboutRef.current.scrollHeight > 114);
    }
  }, [data?.about]);

  if (!data) {
    return (
      <S.Container>
        <MetaTags title={t(SPONSORS_TITLE_KEY)} />
        <SkeletonLoader />
      </S.Container>
    );
  }

  const socialLinks = SOCIAL_LINKS.filter(({ key }) => !!data[key]);
  const hasSocialLinks = socialLinks.length > 0;

  return (
    <S.Container>
      <MetaTags title={data.name || t(SPONSORS_TITLE_KEY)} />

      <HeroBannerSection
        data={data}
        categoryColor={categoryColor}
        onGoBack={() => history.goBack()}
        onContact={toggleShouldDisplayContactUs}
      />

      {/* CONTENT */}
      <S.ContentWrapper>
        <S.MainContent>
          {/* About */}
          <S.Card>
            <S.CardTitle>{t('pages.sponsors.about')}</S.CardTitle>
            <S.AboutText
              ref={aboutRef}
              $expanded={showFullAbout}
              $collapsible={aboutNeedsExpand}
            >
              <div dangerouslySetInnerHTML={{ __html: data.about }} />
              {aboutNeedsExpand && !showFullAbout && <S.AboutFade />}
            </S.AboutText>
            {aboutNeedsExpand && (
              <S.ReadMoreButton
                type="button"
                $color={categoryColor}
                onClick={() => setShowFullAbout((prev) => !prev)}
              >
                {showFullAbout ? 'Ver menos' : 'Ler mais'}
                <FaChevronDown
                  size={10}
                  style={{
                    transform: showFullAbout ? 'rotate(180deg)' : 'none',
                  }}
                />
              </S.ReadMoreButton>
            )}
          </S.Card>

          {/* Videos */}
          {!!data.videos?.data.length && (
            <S.Card>
              <S.CardTitle>{t('pages.sponsors.videos')}</S.CardTitle>
              <SponsorVideos
                videos={data.videos.data.filter((video) => !!video.status)}
                color={categoryColor}
              />
            </S.Card>
          )}

          {/* Archives */}
          {!!data.archives.data.length && (
            <S.Card>
              <S.CardTitleWithCount>
                <h2>{t('pages.sponsors.files')}</h2>
                <span>{data.archives_total} arquivos</span>
              </S.CardTitleWithCount>

              <S.FileList>
                {data.archives.data.map((archive) => (
                  <S.FileRow
                    key={archive.id}
                    href={archive.url_download}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <S.FileIcon>
                      <FaFilePdf size={16} color="#DC2626" />
                    </S.FileIcon>
                    <S.FileInfo>
                      <div>{archive.description}</div>
                    </S.FileInfo>
                    <FaDownload size={14} color="#94a3b8" />
                  </S.FileRow>
                ))}
              </S.FileList>
            </S.Card>
          )}

          {/* Products */}
          {!!data.products.data.length && (
            <S.Card>
              <S.CardTitle>{t('pages.sponsors.products')}</S.CardTitle>
              <S.ProductList>
                {data.products.data.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      onClick={handleSelectProduct(product)}
                    >
                      <img src={product.photo} alt={product.name} />
                    </button>
                  </li>
                ))}
              </S.ProductList>
            </S.Card>
          )}
        </S.MainContent>

        <S.Sidebar>
          {/* Social Links */}
          {hasSocialLinks && (
            <S.SidebarCard>
              <S.SidebarCardTitle>Redes Sociais</S.SidebarCardTitle>
              <S.SocialLinksList>
                {socialLinks.map(({ key, Icon }) => (
                  <S.SocialLinkIcon
                    key={key}
                    href={String(data[key])}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon size={18} />
                  </S.SocialLinkIcon>
                ))}
              </S.SocialLinksList>
            </S.SidebarCard>
          )}

          {/* Contact */}
          <S.SidebarCard>
            <S.SidebarCardTitle>Contato</S.SidebarCardTitle>
            <S.ContactButton
              type="button"
              $color={categoryColor}
              onClick={toggleShouldDisplayContactUs}
            >
              <FaEnvelope size={12} />
              Enviar mensagem
            </S.ContactButton>
          </S.SidebarCard>
        </S.Sidebar>
      </S.ContentWrapper>

      <AnimatePresence>
        {activeProduct && (
          <Modal handleClose={handleCloseModal}>
            <ProductModal data={activeProduct} />
          </Modal>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {shouldDisplayContactUs && (
          <Modal handleClose={toggleShouldDisplayContactUs}>
            <ContactUsModal
              handleClose={toggleShouldDisplayContactUs}
              company={data}
            />
          </Modal>
        )}
      </AnimatePresence>
    </S.Container>
  );
};

export default SponsorProfile;
